import type { ChannelModelRecord } from '@nadoutong/contracts';
import { useUpdateChannelModel } from '../../api/useChannelModels.ts';
import { useT } from '../../i18n/index.ts';
import { Switch } from '../../ui/index.ts';
import { useToast, coerceErrorMessage } from '../Toast.tsx';

/**
 * Whether THIS SKU follows per-org namespace-grain rate agreements (#2446).
 *
 * The control does not create, edit, or delete those agreements. It only
 * decides whether this SKU inherits them. Default on is today's behaviour.
 */
export function NamespaceRateToggle({ record }: { readonly record: ChannelModelRecord }): JSX.Element {
  const t = useT();
  const toast = useToast();
  const updateMut = useUpdateChannelModel();
  const on = record.namespace_rate_applies;

  function handleChange(next: boolean): void {
    updateMut.mutate(
      { skuId: record.sku_id, body: { namespace_rate_applies: next } },
      {
        onSuccess: () => toast.success(
          t(next ? 'sku_detail.namespace_rate.toast_follows' : 'sku_detail.namespace_rate.toast_own'),
        ),
        onError: (error) => toast.error(coerceErrorMessage(error)),
      },
    );
  }

  return (
    <div className="flex flex-col gap-2 border-t border-line-soft pt-4" data-testid="sku-namespace-rate-control">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-body text-paper">{t('sku_detail.namespace_rate.label')}</span>
          <span className="text-label text-paper-low">
            {t(on ? 'sku_detail.namespace_rate.state_follows' : 'sku_detail.namespace_rate.state_own')}
          </span>
        </div>
        <Switch
          checked={on}
          onCheckedChange={handleChange}
          disabled={updateMut.isPending}
          testId="sku-namespace-rate-toggle"
        />
      </div>
      <p className="m-0 text-label text-paper-low">
        {t('sku_detail.namespace_rate.hint')}
      </p>
    </div>
  );
}
